'use client';

import type { ReactNode } from 'react';
import { useBreakpoint } from '@/shared/hooks/use-media-query';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import { DesktopShell } from '@/shared/ui';
import { AppSidebar } from './AppSidebar';
import {
  DesktopLayoutProvider,
  useOptionalDesktopLayout,
} from '../context/DesktopLayoutContext';
import { UnifiedRenderPanel } from '@/features/design/render';

interface ProjectDesktopLayoutProps {
  children: ReactNode;
  projectId: string;
  currentConceptId?: string;
}

/**
 * Inner layout component that consumes the context.
 */
function DesktopLayoutInner({
  children,
}: ProjectDesktopLayoutProps) {
  const { isLg, isXl } = useBreakpoint();
  const layoutContext = useOptionalDesktopLayout();
  const sidebarCollapsed = useSidebarStore((s) => s.collapsed);

  const renderSettingsData = layoutContext?.renderSettingsData;
  const showMiddlePanel = isXl && renderSettingsData;

  // When the sidebar is collapsed, DesktopShell reserves a LEFT-only gutter so
  // the page title clears the floating reveal button — but that shoves the
  // page's own `mx-auto` content right. Mirror it with a matching RIGHT padding
  // here so the content re-centers. Padding lives on the SCROLL container, so
  // the scrollbar still pins to the window's right edge (padding is inside the
  // element's border box). Only in single-column mode — with the inspector
  // aside the trailing edge already belongs to the panel, not the scrollbar.
  const collapsedRightGutter =
    isLg && sidebarCollapsed && !showMiddlePanel ? ' pe-14' : '';
  const setTopBarSlot = layoutContext?.setTopBarSlot;

  // The same wrapper tree is rendered at every breakpoint — below `lg` the
  // wrappers collapse to `display: contents` (DesktopShell `bare`, and the
  // `contents` classes here) so they produce no layout, matching the old bare
  // `<>{children}</>` mobile output visually. The point is identity: `children`
  // sit at a STABLE position in the React tree across the `lg` boundary, so a
  // resize no longer unmounts+remounts the content subtree (which destroyed any
  // in-flight local state, e.g. the angle upload/validation modal).
  //
  // <main> stays at slot 0 across renders; the inspector aside sits at
  // slot 1 as a conditional sibling (rendered or `false`). This keeps
  // main's child index stable when showMiddlePanel toggles — otherwise
  // ConceptEditor's mount-effects would re-toggle the structure and
  // loop ("Maximum update depth exceeded").
  return (
    <DesktopShell
      sidebar={<AppSidebar />}
      // Project home (ProjectHeroSection) sets this; concept/editor pages leave
      // it off. Drives the full-bleed teal wash behind the frosted sidebar.
      showTopWash={layoutContext?.showHeroWash ?? false}
      bare={!isLg}
    >
      <div className={isLg ? 'flex-1 min-w-0 flex flex-col gap-4 h-full' : 'contents'}>
        {/* Full-width topbar slot. ConceptEditor (and any future
            two-column screen) portals its page header here so the bar
            stretches over content + inspector aside, matching Apple's
            macOS / iPad layout. `empty:hidden` collapses the slot when
            no consumer is publishing, so single-column screens render
            their header inline as before. Below `lg` the slot is hidden and
            the ref is detached (topBarSlot → null) so ConceptEditor renders
            its header inline, exactly as before the stable-tree change. */}
        <div
          ref={isLg ? setTopBarSlot : null}
          // pt-4 aligns the bar with the sidebar card's `my-4` top edge — the
          // content column itself deliberately reaches the window top (see
          // DesktopShell), so the inset lives on the slot, not the shell.
          className={isLg ? 'empty:hidden flex-shrink-0 pt-4' : 'hidden'}
        />
        <div className={isLg ? 'flex-1 min-h-0 flex gap-4' : 'contents'}>
          {/* Content column — shares the page panel-gray background
              (the album cover / render card is the floating element).
              Apple HIG "inspector" convention places the settings panel
              on the trailing edge, so the content lives on the leading
              side. */}
          {/* `scrollbar-gutter: stable` reserves the classic scrollbar's width
              whether or not the column currently overflows. Without it the
              column's inner width jumps by the scrollbar width the moment the
              content grows past the viewport — and anything sized from that
              width (the angle image box, the card frame's strip padding) feeds
              a height change straight back into the overflow test. At the
              viewport proportions where that balances, the page strobes between
              the two states. Overlay-scrollbar platforms (default macOS) reserve
              nothing, so this costs them no space. */}
          {/* `data-page-scroll` marks THE scroller a full-viewport overlay has
              to hold still: on a wide window this column scrolls, not the
              document, so a drill-down panel would otherwise sit next to a
              live second scrollbar (see `useOverlayScrollLock`). */}
          <main
            data-page-scroll={isLg ? '' : undefined}
            className={
              isLg
                ? `flex-1 min-w-0 overflow-y-auto [scrollbar-gutter:stable]${collapsedRightGutter}`
                : 'contents'
            }
          >
            {children}
          </main>
          {showMiddlePanel && (
            // Inspector column has no chrome of its own — no fill, no
            // border, no shadow. SettingsCard groups (`bg-settings-card`)
            // float directly on the page panel-gray.
            <aside className="w-[350px] flex-shrink-0 h-full overflow-hidden flex flex-col me-4">
              <UnifiedRenderPanel
                project={renderSettingsData.project}
                concept={renderSettingsData.concept}
                conceptAngles={renderSettingsData.conceptAngles}
                angles={renderSettingsData.angles}
                renders={renderSettingsData.renders}
                controls={renderSettingsData.controls}
                angleId={renderSettingsData.angleId}
                currentRenderId={renderSettingsData.currentRenderId}
                onRefetch={renderSettingsData.onRefetch}
              />
            </aside>
          )}
        </div>
      </div>
    </DesktopShell>
  );
}

/**
 * Desktop layout wrapper for project and concept pages.
 *
 * Breakpoint behavior:
 * - < lg (1024px): Mobile layout, renders children directly
 * - lg-xl (1024-1280px): 2-column layout (sidebar + content)
 * - >= xl (1280px): 3-pane layout (sidebar + content + inspector settings)
 */
export function ProjectDesktopLayout({
  children,
  projectId,
  currentConceptId,
}: ProjectDesktopLayoutProps) {
  return (
    <DesktopLayoutProvider>
      <DesktopLayoutInner
        projectId={projectId}
        currentConceptId={currentConceptId}
      >
        {children}
      </DesktopLayoutInner>
    </DesktopLayoutProvider>
  );
}
